import '.s{display:inline-flex;}.ssr-plugin'
import '.c{align-items:center;}.ssr-plugin'
import '.t{margin-bottom:1rem;}.ssr-plugin'
import { Title } from '@app/components/core/typography/Title'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { DashboardPageLayout } from '@app/pages/layout/DashboardPageLayout'
import { useTranslations } from '@i18n/useLanguage'
import { useAuthState, useClient } from '@based/sdk'
import { useState, useEffect, useCallback } from 'react'
import { fullName, partitionName } from '@app/util/helpers'
import { TextEditPanel } from './sections/shared/TextEditPanel'
import { ProfileEdit } from 'functions/app/dashboard/profile/edit'
import { ImageEditPanel } from './sections/shared/ImageEditPanel'
import { MainInfoEditPanel } from './sections/provileView/MainInfoEditPanel'
import { AboutInfoEditPanel } from './sections/provileView/AboutInfoEditPanel'
import { ProfileTagsEditPanels } from './sections/provileView/ProfileTagsEditPanels'
import { useToast } from '@app/util/hooks/useToast'
import { PersonalProfileModel } from 'functions/app/dashboard/profile/view'
import { Tooltip } from '@app/components/core/tooltip/Tooltip'
import { styled } from 'inlines'
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons'
import { Icon } from '@app/components/core/icon/Icon'

export const ProfileView = () => {
  const client = useClient()
  const t = useTranslations().app
  const translations = t.dashboard.profile
  const authState = useAuthState()
  const [profile, setProfile] = useState<PersonalProfileModel>()
  const { showSuccessToast, showFailToast } = useToast()

  useEffect(() => {
    client
      .query('view-personal-profile', { id: authState.userId })
      .subscribe((result) => setProfile(result))
  }, [authState.userId, client])

  const save = useCallback(
    async (value: ProfileEdit) => {
      return await client
        .call('edit-profile', { id: authState.userId, value })
        .then((success) => {
          if (success) {
            showSuccessToast(translations.profileChangedSavedToast, 1.5)
          } else {
            showFailToast()
          }
          return success
        })
        .catch((e) => {
          showFailToast()
          throw new Error(e)
        })
    },
    [
      authState.userId,
      client,
      showSuccessToast,
      translations.profileChangedSavedToast,
      showFailToast,
    ],
  )

  const saveMainInfo = async (
    name?: string,
    email?: string,
    avatar?: File,
    banner?: File,
  ): Promise<boolean> => {
    if (name) {
      const partitionedName = partitionName(name)
      await save({
        firstName: partitionedName.firstName,
        lastName: partitionedName.lastName,
        middlePart: partitionedName.middlePart,
      })
    }

    const avatarUpload = avatar
      ? client.stream('upload-file', {
          mimeType: avatar.type,
          contents: avatar,
          size: avatar.size,
          fileName: `profile-avatar-${Date.now()}-${avatar.name}`,
          payload: { profileAvatar: { id: authState.userId } },
        })
      : Promise.resolve()

    const bannerUpload = banner
      ? client.stream('upload-file', {
          mimeType: banner.type,
          contents: banner,
          size: banner.size,
          fileName: `profile-banner-${Date.now()}-${banner.name}`,
          payload: { profileBanner: { id: authState.userId } },
        })
      : Promise.resolve()
    await Promise.all([avatarUpload, bannerUpload])

    return true // TODO: promise fails atm due to some selvaId error
  }

  const saveBio = async (bio: string) => {
    return await save({ bio })
  }

  const saveGallery = async (
    deleted: number[],
    added: File[],
  ): Promise<boolean> => {
    deleted.forEach((fileId) => void client.call('delete-file', { fileId }))
    const uploads = added.map(async (file) => {
      return client.stream('upload-file', {
        mimeType: file.type,
        contents: file,
        size: file.size,
        fileName: `profile-media-${Date.now()}-${file.name}`,
        payload: { profileMedia: { id: authState.userId } },
      })
    })
    await Promise.all(uploads)
    return true // TODO: promise fails atm due to some selvaId error
  }

  if (!profile) {
    return (
      <DashboardPageLayout>
        <Title.H3>{translations.title}</Title.H3>
      </DashboardPageLayout>
    )
  }

  const saveAbout = async (
    ageCategory: number | null,
    residenceId: number,
    numberOfAdults: number,
    numberOfChildren: number,
  ) => {
    return await save({
      ageCategory: ageCategory ? { id: ageCategory } : null,
      ...(residenceId > 0 ? { location: { id: residenceId } } : {}),
      numberOfAdults,
      numberOfChildren,
    })
  }

  const name = fullName(profile)

  return (
    <DashboardPageLayout gap="0">
      <div className="s c t"
      >
        <Title.H3 style={{ marginBottom: '0' }}>{translations.title}</Title.H3>
        <Tooltip content={translations.profileTooltip} position="right">
          <Icon
            icon={faCircleInfo}
            style={{
              color: 'var(--shades-blue-200)',
              '&:hover': { color: 'var(--primary-blue)' },
              '&:focus-visible': { color: 'var(--primary-blue)' },
            }}
          />
        </Tooltip>
      </div>
      <Row style={{ alignItems: 'start', flexWrap: 'wrap' }}>
        <Column alignItems="stretch" style={{ flex: '1 1 30rem' }}>
          <MainInfoEditPanel
            name={name}
            email={profile.email}
            banner={profile.banner?.src}
            avatar={profile.avatar?.src}
            onSave={saveMainInfo}
          />
          <TextEditPanel
            title={translations.bio}
            editHint={translations.bioEditHint}
            value={profile.bio}
            onSave={saveBio}
          />
          <ImageEditPanel
            title={translations.pictures}
            images={profile.media.map((i, idx) => ({
              id: (i as { id?: number }).id ?? idx,
              src: typeof i === 'string' ? i : i.src,
            }))}
            onSave={saveGallery}
          />
        </Column>
        <Column alignItems="stretch" style={{ flex: '1 1 20rem' }}>
          <AboutInfoEditPanel profile={profile} onSave={saveAbout} />
          <ProfileTagsEditPanels profile={profile} />
        </Column>
      </Row>
    </DashboardPageLayout>
  )
}
